import { useEffect, useState } from 'react';
import * as React from 'react';

export interface LazyImageProps<TContainer> {
  alt: string;
  className?: string;
  render: React.FC<LazyImageRenderProps<TContainer>>;
  src: string;
}

export type LazyImageRenderProps<TContainer> = {
  containerRef: React.Ref<TContainer>;
} & (
  | {
      image: HTMLImageElement;
      isReady: true;
    }
  | {
      image: undefined;
      isReady: false;
    }
);

/**
 * This component preloads an image and appends it to the container gathered
 * from the `render` callback when it finishes loading.
 */
export function LazyImage<TContainer extends HTMLElement>({
  alt,
  className = '',
  render: Renderer,
  src,
}: LazyImageProps<TContainer>) {
  const [prefetchedImage, setPrefetchedImage] = useState<HTMLImageElement | null>(null);
  const [container, setContainer] = useState<TContainer | null>(null);

  // Prefetch the image
  useEffect(() => {
    if (!src) {
      return;
    }

    const handleLoad = () => {
      setPrefetchedImage(image);
    };

    const image = new Image();
    image.addEventListener('load', handleLoad);
    image.src = src;

    return () => {
      setPrefetchedImage(null);

      image.removeEventListener('load', handleLoad);
      image.src = '';
    };
  }, [src]);

  // Append the prefetched image to the container
  useEffect(() => {
    if (!container || !prefetchedImage) {
      return;
    }

    container.appendChild(prefetchedImage);

    return () => {
      prefetchedImage.parentNode?.removeChild(prefetchedImage);
    };
  }, [container, prefetchedImage]);

  // Update the attributes of the prefetched image
  useEffect(() => {
    if (!prefetchedImage) {
      return;
    }

    prefetchedImage.alt = alt;
    prefetchedImage.className = className;
  }, [alt, className, prefetchedImage]);

  if (prefetchedImage) {
    return <Renderer containerRef={setContainer} image={prefetchedImage} isReady />;
  }

  return <Renderer containerRef={setContainer} image={undefined} isReady={false} />;
}
